// src/components/store/sections/hero-banner/HeroSplitScreen.tsx
"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Sparkles, ArrowRight, ShieldCheck, Truck, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";

export interface HeroBannerItem {
  id: number;
  badge_title?: string;
  title: string;
  subtitle?: string;
  button_text?: string;
  target_url?: string;
  image?: string | null;
  font_family?: string;
  title_color?: string;
  subtitle_color?: string;
  bg_color?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_direction?: string;
}

interface HeroBannerProps {
  banners?: HeroBannerItem[];
  primaryColor?: string;
}

export default function HeroSplitScreen({
  banners = [],
  primaryColor = "#059669",
}: HeroBannerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!banners || banners.length === 0) {
    return null;
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
  };

  const banner = banners[currentIndex] || banners[0];

  const getBannerBackgroundStyle = (item?: HeroBannerItem | null) => {
    if (!item) return {};
    const dir = item.gradient_direction || "to bottom right";
    const c1 = item.gradient_color_1;
    const c2 = item.gradient_color_2;
    const c3 = item.gradient_color_3;

    if (c1 && c2 && c3) {
      return { background: `linear-gradient(${dir}, ${c1}, ${c2}, ${c3})` };
    }
    if (c1 && c2) {
      return { background: `linear-gradient(${dir}, ${c1}, ${c2})` };
    }
    if (item.bg_color) {
      return { backgroundColor: item.bg_color };
    }
    return {};
  };

  const formatImageUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const imageUrl = banner ? formatImageUrl(banner.image) : null;
  const bannerFont = banner?.font_family || "Inter, sans-serif";
  const customBgStyle = getBannerBackgroundStyle(banner);
  const hasCustomBg = Boolean(banner?.bg_color || banner?.gradient_color_1);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 relative group">
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Anek+Bangla:wght@400;600;800&family=Bebas+Neue&family=Black+Ops+One&family=Cinzel:wght@600;800&family=Galada&family=Hind+Siliguri:wght@400;600;700&family=Inter:wght@400;700;900&family=Mina:wght@400;700&family=Montserrat:wght@400;700;900&family=Noto+Sans+Bengali:wght@400;700&family=Oswald:wght@500;700&family=Playfair+Display:wght@600;800&family=Plus+Jakarta+Sans:wght@600;800&family=Poppins:wght@400;600;800&family=Roboto:wght@400;700&family=Syne:wght@700;800&display=swap"
        rel="stylesheet"
      />

      <div
        style={customBgStyle}
        className={`w-full relative border-2 border-slate-200 dark:border-slate-800 rounded-[32px] p-6 sm:p-12 shadow-2xl transition-all overflow-hidden ${
          !hasCustomBg
            ? "bg-gradient-to-br from-slate-100 via-white to-slate-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 text-slate-900 dark:text-white"
            : "text-white"
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center z-10 relative">
          
          {/* Left Text */}
          <div className="lg:col-span-7 space-y-6">
            <span
              style={{
                backgroundColor: `${primaryColor}20`,
                color: primaryColor,
                borderColor: `${primaryColor}40`,
              }}
              className="px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5 border"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>{banner.badge_title || "Top Deal Collection"}</span>
            </span>

            <h1
              style={{
                fontFamily: bannerFont,
                color: banner.title_color || (hasCustomBg ? "#ffffff" : undefined),
              }}
              className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.15]"
            >
              {banner.title || "Discover Quality Essentials"}
            </h1>

            <p
              style={{
                fontFamily: bannerFont,
                color: banner.subtitle_color || (hasCustomBg ? "#cbd5e1" : undefined),
              }}
              className="text-sm sm:text-base font-medium max-w-xl leading-relaxed opacity-90"
            >
              {banner.subtitle ||
                "Explore premium curated categories, best prices, and same-day delivery right to your door."}
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Link
                href={banner.target_url || "/products"}
                style={{ backgroundColor: primaryColor }}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-2xl text-white text-xs sm:text-sm font-black shadow-lg hover:opacity-95 active:scale-95 transition-all cursor-pointer"
              >
                <span>{banner.button_text || "Shop The Collection"}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10 text-xs font-bold opacity-80">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-emerald-400" />
                <span>Fast Track Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>100% Authentic Quality</span>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="lg:col-span-5 relative w-full h-[320px] sm:h-[400px] rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-black/20 backdrop-blur-xs flex items-center justify-center">
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={banner.title || "Banner Visual"}
                fill
                unoptimized
                priority
                className="object-contain p-4 hover:scale-105 transition-transform duration-700 ease-out drop-shadow-2xl"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-400 gap-2">
                <ShoppingBag className="w-12 h-12 opacity-50" />
                <span className="text-xs font-bold font-mono">Product Showcase Image</span>
              </div>
            )}
          </div>
        </div>

        {/* 🌟 বাম পাশের অ্যারো বাটন */}
        {banners.length > 1 && (
          <button
            onClick={prevSlide}
            type="button"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-2xl border border-slate-200 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer backdrop-blur-xs"
            aria-label="Previous Slide"
          >
            <ChevronLeft className="w-6 h-6 stroke-[2.5]" />
          </button>
        )}

        {/* 🌟 ডান পাশের অ্যারো বাটন */}
        {banners.length > 1 && (
          <button
            onClick={nextSlide}
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-2xl border border-slate-200 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer backdrop-blur-xs"
            aria-label="Next Slide"
          >
            <ChevronRight className="w-6 h-6 stroke-[2.5]" />
          </button>
        )}
      </div>
    </section>
  );
}